import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { format } from 'date-fns';

import { isTruthyArray } from '~/utils/isTruthyArray';

import { PaymentError } from '../../types/error';
import {
  CreditCardType,
  PaymentFrequencyOptions,
  PaymentMethodOptions,
} from '../../types/payments';
import { Nullable } from '../../types/utils';
import { Payments, PaymentState, PaymentTransactionDetail } from './types';

const initialState: Nullable<Payments> = {
  isPaymentInProgress: false,
  paymentFrequency: PaymentFrequencyOptions.OneOff,
  creditCardName: null,
  creditCardNumberMasked: null,
  creditCardType: null,
  cybersourceToken: null,
  creditCardCVV: null,
  creditCardExpiryDate: null,
  paymentMethod: PaymentMethodOptions.CreditCard,
  bankAccountName: null,
  bankAccountBsb: null,
  bankAccountNumber: null,
  errors: [],
  isCreditCardValid: false,
  /** @note isValid is for the entire step whereas isCreditCardValid is only for credit card field */
  isValid: false,
  isValidCVC: false,
  paymentState: 'init',
  paymentReference: null,
  transactionDetail: null,
  transientToken: null,
};

const validate = (state: Nullable<Payments>) => {
  const validCases: boolean[] = [];

  validCases.push(isTruthyArray([state.paymentFrequency, state.paymentMethod]));

  if (state.paymentMethod === PaymentMethodOptions.CreditCard) {
    if (state.transientToken) {
      validCases.push(
        isTruthyArray([state.transientToken, state.creditCardType, state.creditCardName])
      );
    } else {
      validCases.push(
        isTruthyArray([state.creditCardName, state.creditCardCVV, state.creditCardExpiryDate])
      );

      if (!state.creditCardType) validCases.push(false);

      state.isValidCVC =
        state.creditCardType === CreditCardType.Amex
          ? state.creditCardCVV?.length === 4
          : state.creditCardCVV?.length === 3;

      if (!state.isValidCVC) validCases.push(false);
      if (!state.isCreditCardValid) validCases.push(false);
    }
  }

  if (state.paymentMethod === PaymentMethodOptions.BankAccount) {
    validCases.push(
      isTruthyArray([state.bankAccountName, state.bankAccountBsb, state.bankAccountNumber])
    );
  }

  state.isValid = validCases.every((v) => v);
};

export const paymentsSlice = createSlice({
  name: 'payments',
  initialState,
  reducers: {
    /*
     * Redux Toolkit allows us to write "mutating" logic in reducers. It doesn't actually mutate the state
     * because it uses the Immer library, which detects changes to a "draft state" and produces a brand new
     * immutable state based off those changes
     */
    updatePaymentFrequency: (state, action: PayloadAction<PaymentFrequencyOptions | null>) => {
      state.paymentFrequency = action.payload;
      validate(state);
    },
    updatePaymentMethod: (state, action: PayloadAction<PaymentMethodOptions | null>) => {
      state.paymentMethod = action.payload;
      validate(state);
    },
    updateCreditCardName: (state, action: PayloadAction<string | null>) => {
      state.creditCardName = action.payload;
      validate(state);
    },
    updateCreditCardNumberMasked: (state, action: PayloadAction<string | null>) => {
      state.creditCardNumberMasked = action.payload;
      validate(state);
    },
    updateCreditCardType: (state, action: PayloadAction<CreditCardType | null>) => {
      state.creditCardType = action.payload;
      validate(state);
    },
    updateCybersourceToken: (state, action: PayloadAction<string | null>) => {
      state.cybersourceToken = action.payload;
      validate(state);
    },
    updateCreditCardCVV: (state, action: PayloadAction<string | null>) => {
      state.creditCardCVV = action.payload;
      validate(state);
    },
    updateCreditCardExpiryDate: (state, action: PayloadAction<string | Date | null>) => {
      if (action.payload === null || typeof action.payload === 'string') {
        state.creditCardExpiryDate = action.payload;
      } else {
        state.creditCardExpiryDate = format(action.payload, 'MMyyyy');
      }
      validate(state);
    },
    updateBankAccountName: (state, action: PayloadAction<string | null>) => {
      state.bankAccountName = action.payload;
      validate(state);
    },
    updateBankAccountBsb: (state, action: PayloadAction<string | null>) => {
      state.bankAccountBsb = action.payload;
      validate(state);
    },
    updateBankAccountNumber: (state, action: PayloadAction<string | null>) => {
      state.bankAccountNumber = action.payload;
      validate(state);
    },
    updateIsCreditCardValid: (state, action: PayloadAction<boolean | null>) => {
      state.isCreditCardValid = action.payload;
      validate(state);
    },
    addPaymentError: (state, action: PayloadAction<PaymentError>) => {
      state.errors?.push(action.payload);
      validate(state);
    },
    clearPaymentErrors: (state) => {
      state.errors = [];
      validate(state);
    },
    resetCreditCardForm: (state) => {
      state.creditCardName = null;
      state.creditCardNumberMasked = null;
      state.creditCardCVV = null;
      state.creditCardExpiryDate = null;
      state.isCreditCardValid = false;
      state.isValidCVC = false;
      validate(state);
    },
    resetBankAccountForm: (state) => {
      state.bankAccountName = null;
      state.bankAccountBsb = null;
      state.bankAccountNumber = null;
      validate(state);
    },
    updateIsPaymentInProgress: (state, action: PayloadAction<boolean>) => {
      state.isPaymentInProgress = action.payload;
      validate(state);
    },
    updatePaymentState: (state, action: PayloadAction<PaymentState>) => {
      state.paymentState = action.payload;
    },
    updatePaymentReference: (state, action: PayloadAction<string>) => {
      state.paymentReference = action.payload;
    },
    updateTransactionDetail: (
      state,
      action: PayloadAction<PaymentTransactionDetail | undefined>
    ) => {
      state.transactionDetail = action.payload ?? null;
    },
    updateTransientToken: (state, action: PayloadAction<string | null>) => {
      state.transientToken = action.payload;
      validate(state);
    },
  },
});
